import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Crosshair, FileText, Folder, TrendingUp } from 'lucide-react';
import { Pill, Sparkline, type Assignment, type CurrentFocus, type EmployeeView } from '../shared';
import { buttonStyles } from '../../../components/tailgrids/core/button.js';
import { ProfileCard } from './ProfileCard.js';

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// ── Current focus hero ──────────────────────────────────────────────────────

export const CurrentFocusHero: React.FC<{ focus: CurrentFocus; employeeId: string; name: string }> = ({
  focus,
  employeeId,
  name,
}) => {
  if (!focus.label) {
    return (
      <div className="mb-4 overflow-hidden rounded-xl border border-border bg-surface">
        <div className="border-b border-border bg-linear-to-br from-accent/10 to-transparent px-[18px] py-4">
          <div className="flex items-center gap-2.5 font-mono text-[11px] text-muted">
            <Crosshair size={13} /> CURRENT FOCUS
          </div>
          <h3 className="mb-1 mt-2.5 text-[17px] font-semibold leading-snug text-faint">No current focus</h3>
          <p className="text-xs text-muted">
            {name} isn't actively focused on a workstream. Assign them to a project to give them focus.
          </p>
        </div>
      </div>
    );
  }
  return (
    <div className="mb-4 overflow-hidden rounded-xl border border-border bg-surface">
      <div className="border-b border-border bg-linear-to-br from-accent/10 to-transparent px-[18px] py-4">
        <div className="flex items-center gap-2.5 font-mono text-[11px] text-muted">
          <Crosshair size={13} /> CURRENT FOCUS{focus.projectName ? ` · ${focus.projectName}` : ''}
        </div>
        <h3 className="mb-1 mt-2.5 text-[17px] font-semibold leading-snug text-heading">{focus.label}</h3>
        <div className="mt-3 flex gap-2">
          {focus.projectId && (
            <Link
              to={`/projects/${focus.projectId}/workforce/${employeeId}`}
              className={buttonStyles({ variant: 'primary', appearance: 'fill', size: 'sm' })}
            >
              <ArrowRight size={12} /> Open in project
            </Link>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Assignments & history ───────────────────────────────────────────────────

export const AssignmentsHistory: React.FC<{ assignments: Assignment[]; employeeId: string; name: string }> = ({
  assignments,
  employeeId,
  name,
}) => {
  const active = assignments.filter((a) => a.active);
  const ended = assignments.filter((a) => !a.active);
  return (
    <ProfileCard
      className="mb-4"
      icon={<Folder size={14} />}
      title="Assignments & history"
      meta={`${active.length} active · ${ended.length} ended`}
    >
      {assignments.length === 0 ? (
        <div className="px-4 py-6 text-xs text-muted">
          No project assignments yet. Assign {name} to a project to deploy them.
        </div>
      ) : (
        <>
          {active.map((a, i) => (
            <div
              key={a.projectId}
              className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-border px-4 py-3 last:border-b-0 hover:bg-surface-hover"
            >
              <span className="h-[9px] w-[9px] rounded-xs bg-accent" aria-hidden="true" />
              <div className="min-w-0">
                <div className="flex items-center gap-1.5 text-[13px] font-medium text-heading">
                  {a.projectName}
                  {/* The most-recently-active assignment is the "current" one (accent); others are "active" (teal). */}
                  {i === 0 ? (
                    <Pill tone="accent" dot size="xs">
                      current
                    </Pill>
                  ) : (
                    <Pill tone="teal" size="xs">
                      active
                    </Pill>
                  )}
                </div>
                {(a.team || a.since) && (
                  <div className="mt-0.5 font-mono text-[10.5px] text-faint">
                    {[a.team, a.since ? `since ${fmtDate(a.since)}` : null].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              <Link
                to={`/projects/${a.projectId}/workforce/${employeeId}`}
                className="inline-flex items-center gap-1 text-[11.5px] text-accent-text hover:underline"
              >
                Open in project <ArrowRight size={12} />
              </Link>
            </div>
          ))}
          {ended.map((a) => (
            <div
              key={a.projectId}
              className="grid grid-cols-[auto_1fr_auto] items-center gap-3 border-b border-border px-4 py-3 opacity-80 last:border-b-0 hover:bg-surface-hover"
            >
              <span className="h-[9px] w-[9px] rounded-xs bg-faint" aria-hidden="true" />
              <div className="min-w-0">
                <div className="text-[13px] font-medium text-muted">{a.projectName}</div>
                {(a.team || a.endedAt) && (
                  <div className="mt-0.5 font-mono text-[10.5px] text-faint">
                    {[a.team, a.endedAt ? `ended ${fmtDate(a.endedAt)}` : null].filter(Boolean).join(' · ')}
                  </div>
                )}
              </div>
              <Link
                to={`/projects/${a.projectId}/workforce/${employeeId}`}
                className="inline-flex items-center gap-1 text-[11.5px] text-accent-text hover:underline"
              >
                View <ArrowRight size={12} />
              </Link>
            </div>
          ))}
        </>
      )}
    </ProfileCard>
  );
};

// ── Performance (org-wide, real data only) ──────────────────────────────────

interface PerfTile {
  label: string;
  value: string;
  series: number[];
  color: string;
}

function numbersFromLog(log: unknown, key: string): number[] {
  if (!Array.isArray(log)) return [];
  return log
    .map((e) => (e && typeof e === 'object' ? Number((e as Record<string, unknown>)[key]) : Number.NaN))
    .filter((n) => Number.isFinite(n));
}

export const PerformanceCard: React.FC<{ employee: EmployeeView }> = ({ employee }) => {
  // Real series only — drawn from the employee's reflection log (per-run quality
  // and duration). No synthetic data: tiles with no backing value show "—" and
  // tiles with no backing series omit the sparkline entirely.
  const log = (employee.raw as Record<string, unknown>).reflectionLog;
  const quality = numbersFromLog(log, 'quality_rating');
  const durations = numbersFromLog(log, 'duration_ms').map((ms) => ms / 1000);
  const tiles: PerfTile[] = [
    {
      label: 'Reliability',
      value: employee.reliability > 0 ? `${Math.round(employee.reliability * 100)}%` : '—',
      series: quality,
      color: 'var(--color-success-text)',
    },
    {
      label: 'Pass rate',
      value: employee.passRate > 0 ? `${Math.round(employee.passRate * 100)}%` : '—',
      series: quality,
      color: 'var(--color-accent-text)',
    },
    {
      label: 'Latency p95',
      value: employee.latencyMs > 0 ? `${(employee.latencyMs / 1000).toFixed(1)}s` : '—',
      series: durations,
      color: 'var(--color-warning-text)',
    },
    // Cost has no per-employee source today (daemon runs carry no cost column) — shown honestly as no-data.
    { label: 'Cost · 7d', value: '—', series: [], color: 'var(--color-muted)' },
  ];
  return (
    <ProfileCard className="mb-4" icon={<TrendingUp size={14} />} title="Performance · org-wide" meta="all assignments">
      <div className="grid grid-cols-2 gap-3 p-4 lg:grid-cols-4">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-[10px] border border-border bg-surface-alt px-3.5 py-[18px]">
            <div className="font-mono text-[10px] uppercase tracking-wide text-faint">{t.label}</div>
            <div className="mt-1.5 font-mono text-[22px] font-semibold leading-none text-heading">{t.value}</div>
            {t.series.length >= 2 && (
              <div className="mt-2.5">
                <Sparkline points={t.series} color={t.color} width={120} height={26} ariaLabel={`${t.label} trend`} />
              </div>
            )}
          </div>
        ))}
      </div>
    </ProfileCard>
  );
};

// ── Outputs across projects ─────────────────────────────────────────────────

export const OutputsCard: React.FC<{ name: string }> = ({ name }) => (
  // There is no employee-level outputs rollup endpoint yet; per-run reports/artifacts
  // use daemon vocabulary that must not leak onto workforce surfaces. Honest empty
  // state until a project-tagged employee-output feed exists.
  <ProfileCard className="mb-4" icon={<FileText size={14} />} title="Outputs across projects">
    <div className="px-4 py-6 text-xs text-muted">
      No outputs recorded across projects yet. Artifacts, completed tasks, and decisions {name} produces in projects
      will appear here, each linking back into its project.
    </div>
  </ProfileCard>
);
